import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { CameraScanner } from '../fulfillment/CameraScanner';
import { cameraScanSupported } from '../fulfillment/camera-support';
import { useScanFeedback } from '../fulfillment/use-scan-feedback';

export type ScanOutcome = { tone: 'accepted' | 'rejected'; text: string; action?: ReactNode };

/**
 * A scan-first input: keyboard-wedge scanners type the code and press Enter,
 * and the camera feeds the same handler. The field stays focused (read-only,
 * not disabled, while busy) so the next scan isn't dropped. Closing the
 * camera releases it.
 */
export function ScanField({
  id,
  label,
  hint,
  busy = false,
  autoFocus = false,
  onScan,
  children,
}: {
  id: string;
  label: string;
  hint: string;
  busy?: boolean;
  autoFocus?: boolean;
  onScan: (code: string) => Promise<ScanOutcome | null> | ScanOutcome | null;
  /** Extra controls in the form row, e.g. a quantity. */
  children?: ReactNode;
}) {
  const input = useRef<HTMLInputElement>(null);
  const sound = useScanFeedback();
  const [code, setCode] = useState('');
  const [camera, setCamera] = useState(false);
  const [outcome, setOutcome] = useState<ScanOutcome | null>(null);
  const [working, setWorking] = useState(false);
  const pending = busy || working;

  useEffect(() => {
    if (autoFocus) input.current?.focus();
  }, [autoFocus]);

  async function submit(value: string): Promise<void> {
    const trimmed = value.trim();
    if (!trimmed || pending) return;
    setWorking(true);
    try {
      const result = await onScan(trimmed);
      setOutcome(result);
      if (result?.tone === 'accepted') sound.accepted();
      if (result?.tone === 'rejected') sound.rejected();
      setCode('');
    } finally {
      setWorking(false);
      input.current?.focus();
    }
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    void submit(code);
  }

  return (
    <div className="intake-scan">
      <form className="scan-form" onSubmit={handleSubmit} aria-label={label}>
        <label className="scan-field">
          <span>{label}</span>
          <input
            ref={input}
            id={id}
            value={code}
            onChange={(event) => setCode(event.target.value)}
            autoComplete="off"
            autoCapitalize="characters"
            spellCheck={false}
            enterKeyHint="send"
            aria-describedby={`${id}-hint`}
            readOnly={pending}
            aria-busy={pending}
          />
        </label>
        {children}
        <button type="submit" className="primary" disabled={pending}>
          {pending ? 'Checking…' : 'Enter'}
        </button>
      </form>
      <p id={`${id}-hint`} className="field-hint">
        {hint}
      </p>
      {cameraScanSupported() ? (
        <div className="scan-tools">
          <button type="button" onClick={() => setCamera((open) => !open)} aria-pressed={camera}>
            {camera ? 'Hide camera' : 'Scan with camera'}
          </button>
        </div>
      ) : null}
      {camera ? (
        <CameraScanner
          onDetected={(detected) => void submit(detected)}
          onClose={() => setCamera(false)}
        />
      ) : null}
      <div className="scan-feedback-region" aria-live="assertive" aria-atomic="true">
        {outcome ? (
          <div className={`scan-feedback scan-feedback-${outcome.tone}`}>
            <strong>{outcome.tone === 'accepted' ? 'Found' : 'Not found'}</strong>
            <span>{outcome.text}</span>
            {outcome.action}
          </div>
        ) : null}
      </div>
    </div>
  );
}
